import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BookOpen, ArrowRight, GraduationCap, Sparkles, Layers, AlertTriangle, LifeBuoy } from "lucide-react";

export default function StudentCourses() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [suspended, setSuspended] = useState([]);
  const [loaded, setLoaded] = useState(false);
  // Abstract Exam access is independent from course enrollment — it lives in the
  // Library alongside the courses so students can dive into pattern practice
  // without leaving this page.
  const [abstract, setAbstract] = useState(null);

  useEffect(() => {
    // Use /student/my-enrollments (real enrollments only, with suspended
    // flags) instead of /courses — /courses falls back to "all active
    // courses" when a student has no enrollments so the profile-completion
    // dropdown can populate, which incorrectly makes a deleted-enrollment
    // student see the fallback list here.
    api.get("/student/my-enrollments")
      .then((r) => {
        const all = r.data?.enrolled || [];
        setItems(all.filter((c) => !c.suspended));
        setSuspended(all.filter((c) => c.suspended));
      })
      .finally(() => setLoaded(true));
    api.get("/student/abstract-exam/status")
      .then((r) => setAbstract(r.data))
      .catch(() => setAbstract({ has_access: false }));
  }, []);

  const hasAbstract = !!abstract?.has_access;
  const hasActive = items.length > 0 || hasAbstract;
  const allSuspended = loaded && !hasActive && suspended.length > 0;
  const showEmpty = loaded && !hasActive && suspended.length === 0;

  return (
    <div className="space-y-6" data-testid="student-courses-page">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 dark:text-blue-400">Library</div>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1 dark:text-slate-50">Available Courses</h1>
      </div>

      {allSuspended ? (
        <Card
          className="p-8 sm:p-10 border-2 border-amber-200 dark:border-amber-800 bg-gradient-to-br from-amber-50 via-orange-50 to-white dark:from-amber-950/40 dark:via-orange-950/30 dark:to-slate-900"
          data-testid="student-courses-suspended-state"
        >
          <div className="flex items-start gap-4">
            <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 grid place-items-center shadow-md shadow-amber-200 dark:shadow-none flex-shrink-0">
              <AlertTriangle className="w-7 h-7 text-white" strokeWidth={1.75} />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-display text-2xl font-bold text-slate-900 dark:text-slate-50">
                Your access is currently suspended
              </h2>
              <div className="mt-3 space-y-2">
                {suspended.map((c) => (
                  <div
                    key={c.id}
                    className="text-sm text-slate-700 dark:text-slate-200"
                    data-testid={`suspended-course-${c.id}`}
                  >
                    Your access to <span className="font-semibold text-amber-900 dark:text-amber-200">{c.course_name}</span>{" "}
                    was suspended
                    {c.suspended_reason ? (
                      <> due to <span className="italic">{c.suspended_reason}</span>.</>
                    ) : (
                      <> by an administrator.</>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Button
                  size="lg"
                  onClick={() => nav("/student/enrollments")}
                  className="h-12 px-6 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white font-bold shadow-md shadow-indigo-200 dark:shadow-none"
                  data-testid="suspended-enroll-cta"
                >
                  <Sparkles className="w-4 h-4 mr-2" strokeWidth={2.25} />
                  Enroll in a Course
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => nav("/student/support")}
                  className="h-12 px-6 border-amber-300 text-amber-900 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-100 dark:hover:bg-amber-950/30 font-bold"
                  data-testid="suspended-contact-support-cta"
                >
                  <LifeBuoy className="w-4 h-4 mr-2" strokeWidth={2.25} />
                  Contact Support
                </Button>
              </div>
            </div>
          </div>
        </Card>
      ) : showEmpty ? (
        <Card
          className="p-8 sm:p-10 border-dashed border-2 border-indigo-200 dark:border-indigo-800 bg-gradient-to-br from-indigo-50 via-violet-50 to-white dark:from-indigo-950/40 dark:via-violet-950/30 dark:to-slate-900 text-center"
          data-testid="student-courses-empty-state"
        >
          <div className="mx-auto h-16 w-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 grid place-items-center shadow-lg shadow-indigo-200 dark:shadow-none mb-4">
            <GraduationCap className="w-8 h-8 text-white" strokeWidth={1.75} />
          </div>
          <h2 className="font-display text-2xl font-bold text-slate-900 dark:text-slate-50">
            You're not enrolled in any course yet
          </h2>
          <p className="text-slate-600 dark:text-slate-300 mt-2 max-w-md mx-auto text-sm">
            Start your board exam journey today — pick a course, grab a free trial,
            and unlock AI-powered review built for the way you study.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center items-center">
            <Link to="/student/enrollments" data-testid="enroll-course-cta">
              <Button
                size="lg"
                className="h-12 px-6 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white font-bold shadow-md shadow-indigo-200 dark:shadow-none"
              >
                <Sparkles className="w-4 h-4 mr-2" strokeWidth={2.25} />
                Enroll in a Course
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
          <div className="mt-5 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-indigo-700 dark:text-indigo-300">
            <span className="h-1.5 w-1.5 rounded-full bg-indigo-500 animate-pulse" />
            Free trial available on every course
          </div>
        </Card>
      ) : (
        <>
          {suspended.length > 0 && (
            <Card
              className="p-4 border-2 border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30"
              data-testid="student-courses-suspended-banner"
            >
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-700 dark:text-amber-300 flex-shrink-0 mt-0.5" strokeWidth={1.75} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-amber-900 dark:text-amber-100 text-sm">
                    Some of your courses are currently suspended
                  </div>
                  <ul className="mt-1 text-xs text-amber-900/90 dark:text-amber-100/90 list-disc list-inside space-y-0.5">
                    {suspended.map((c) => (
                      <li key={c.id} data-testid={`banner-suspended-${c.id}`}>
                        <span className="font-semibold">{c.course_name}</span>
                        {c.suspended_reason ? <> — {c.suspended_reason}</> : null}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-3 flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 border-amber-300 text-amber-900 hover:bg-amber-100/60 dark:border-amber-700 dark:text-amber-100 dark:hover:bg-amber-900/40"
                      onClick={() => nav("/student/support")}
                      data-testid="banner-contact-support-cta"
                    >
                      <LifeBuoy className="w-3.5 h-3.5 mr-1.5" />
                      Contact Support
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          )}
        <div className="grid grid-cols-1 2xl:grid-cols-3 gap-4">
          {/* Abstract Exam standalone module — visually distinct so students
              immediately recognise it isn't a regular course. */}
          {hasAbstract && (
            <Link
              to="/student/abstract-exam"
              className="block"
              data-testid="student-abstract-exam-card"
            >
              <Card className="p-6 border-2 border-violet-200 dark:border-violet-800 bg-gradient-to-br from-indigo-50 via-violet-50 to-white dark:from-indigo-950/40 dark:via-violet-950/30 dark:to-slate-900 hover:shadow-md hover:-translate-y-0.5 transition-all">
                <div className="flex items-start justify-between">
                  <div className="h-10 w-10 rounded-md bg-gradient-to-br from-indigo-500 to-violet-600 text-white grid place-items-center mb-4 shadow-sm">
                    <Layers className="w-5 h-5" strokeWidth={1.75} />
                  </div>
                  <span className="text-[10px] font-extrabold uppercase tracking-widest text-violet-700 dark:text-violet-300 bg-violet-100 dark:bg-violet-900/40 px-2 py-1 rounded-full">
                    Practice
                  </span>
                </div>
                <h3 className="font-display text-lg font-bold dark:text-slate-50">Abstract Exam</h3>
                <p className="text-sm text-slate-600 dark:text-slate-300 mt-1 line-clamp-2">
                  Non-verbal patterns, matrices and visual logic — {abstract.assigned_count} items per session, randomly drawn.
                </p>
                <div className="mt-4 text-sm font-semibold text-violet-700 dark:text-violet-300 inline-flex items-center gap-1">
                  Start <ArrowRight className="w-4 h-4" />
                </div>
              </Card>
            </Link>
          )}

          {items.map((c) => (
            <Link
              to={`/student/courses/${c.id}/subjects`}
              key={c.id}
              className="block"
              data-testid={`student-course-${c.id}`}
            >
              <Card className="p-6 border-slate-200 hover:shadow-md hover:-translate-y-0.5 transition-all">
                <div className="h-10 w-10 rounded-md bg-blue-50 text-blue-700 grid place-items-center mb-4">
                  <BookOpen className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-lg font-bold break-words">{c.course_name}</h3>
                <p className="text-sm text-slate-600 mt-1 line-clamp-2 break-words">{c.description}</p>
                <div className="mt-4 text-sm font-semibold text-blue-700 inline-flex items-center gap-1">
                  Open <ArrowRight className="w-4 h-4" />
                </div>
              </Card>
            </Link>
          ))}
        </div>
        </>
      )}
    </div>
  );
}
